import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import api, { apiPath, absUrl } from "../api/http";

/* Image helpers */
const COVER_FALLBACK = "/images/projects.jpg";
const THUMB_FALLBACK = "/images/placeholder-thumb.jpg";

function SafeImg({ src, alt, className, fallback = COVER_FALLBACK }) {
  const normalized = useMemo(() => {
    if (!src || (typeof src === "string" && !src.trim())) return "";
    return absUrl(src);
  }, [src]);

  const [finalSrc, setFinalSrc] = useState(normalized || fallback);
  useEffect(() => setFinalSrc(normalized || fallback), [normalized, fallback]);

  const onError = useCallback(() => {
    if (finalSrc !== fallback) setFinalSrc(fallback);
  }, [finalSrc, fallback]);

  return (
    <img
      src={finalSrc}
      alt={alt}
      className={className}
      onError={onError}
      loading="lazy"
      decoding="async"
    />
  );
}

/* API */
function normalizeProject(row) {
  const gallery = Array.isArray(row.images) ? row.images.filter(Boolean) : [];
  const cover = row.img || gallery[0] || COVER_FALLBACK;

  return {
    id: row.id,
    title: row.title || row.name || "Untitled Project",
    blurb: row.blurb || row.description || "",
    owner: row.owner || row.client || "—",
    address: row.address || row.location || "—",
    price:
      row.price != null && row.price !== ""
        ? `LKR ${Number(row.price).toLocaleString()}`
        : row.contract_value
        ? `LKR ${Number(row.contract_value).toLocaleString()}`
        : "—",
    img: cover,
    images: [cover, ...gallery.slice(cover === gallery[0] ? 1 : 0)],
  };
}

async function fetchProjects(status) {
  const res = await api.get(apiPath(`/projects?status=${status}&size=100`));
  const payload = res.data || [];
  const list = Array.isArray(payload.data)
    ? payload.data
    : Array.isArray(payload)
    ? payload
    : [];

  return list.map(normalizeProject);
}

/* Presentational Card */
function ProjectCard({ p, onOpen }) {
  return (
    <article>
      <div
        className="border mt-3 rounded-sm overflow-hidden bg-white shadow hover:shadow-lg transition cursor-pointer"
        onClick={() => onOpen(p)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen(p);
          }
        }}
      >
        <SafeImg
          src={p.img}
          alt={`${p.title}${p.owner ? ` – ${p.owner}` : ""}${p.address ? ` – ${p.address}` : ""}`}
          className="h-56 w-full object-cover"
          fallback={COVER_FALLBACK}
        />
        <div className="p-5">
          {/* Title + owner inline */}
          <h3 className="font-bold text-neutral-900">
            {p.title}
            {/* {p.owner && (
              <span className="ml-2 font-semibold text-neutral-700">
                · {p.owner}
              </span>
            )} */}
          </h3>

          {/* Owner — Address line */}
          {(p.owner || p.address) && (
            <p className="mt-1 text-sm text-neutral-500">
              {p.owner && <span className="font-medium">{p.owner}</span>}
              {p.owner && p.address ? " — " : ""}
              {p.address}
            </p>
          )}

          <p className="mt-2 text-sm text-neutral-600 line-clamp-3">{p.blurb}</p>

          <div className="flex justify-end">
            <span className="mt-4 inline-block px-4 py-2 text-xs tracking-wide rounded-sm border border-neutral-900 text-neutral-900 font-semibold">
              VIEW PROJECT
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

/* Modal View */
function ProjectModal({ project, onClose }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [project]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (!project?.images?.length) return;
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % project.images.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + project.images.length) % project.images.length);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, project]);

  if (!project) return null;

  const images = project.images?.length ? project.images : [project.img];
  const goPrev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setIdx((i) => (i + 1) % images.length);

  const hasOverflow = images.length > 5;
  const thumbBase = hasOverflow ? images.slice(0, 4) : images.slice(0, 5);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full max-w-5xl bg-white rounded-lg shadow-2xl overflow-hidden
                   flex flex-col max-h-[90svh] sm:max-h-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 border-b">
          <div>
            <h3 className="text-3xl font-extrabold text-neutral-900">{project.title}</h3>
            <p className="text-sm text-neutral-600 mt-1">{project.blurb}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 inline-flex items-center justify-center w-8 h-6 rounded-sm bg-red-600 text-white hover:bg-red-700"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
              <path d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7 4.3 4.3l6.3 6.3 6.3-6.3 1.4 1.4z" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 flex flex-col gap-6 overflow-y-auto">
          {/* Project Details */}
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="text-sm font-semibold text-neutral-500">Owner</div>
              <div className="col-span-2 text-neutral-900">{project.owner}</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="text-sm font-semibold text-neutral-500">Address</div>
              <div className="col-span-2 text-neutral-900">{project.address}</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="text-sm font-semibold text-neutral-500">Contract Value</div>
              <div className="col-span-2 text-neutral-900">{project.price}</div>
            </div>
          </div>

          {/* Image Viewer */}
          <div className="relative w-full h-56 sm:h-[320px] bg-neutral-100 rounded-xl overflow-hidden flex items-center justify-center">
            {images.length > 1 && (
              <button
                onClick={goPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white grid place-items-center hover:bg-black/70"
                aria-label="Previous image"
              >
                <FaChevronLeft className="w-5 h-5" />
              </button>
            )}

            <SafeImg
              src={images[idx]}
              alt={`${project.title} ${idx + 1}`}
              className="max-h-full max-w-full object-contain"
              fallback={COVER_FALLBACK}
            />

            {images.length > 1 && (
              <button
                onClick={goNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white grid place-items-center hover:bg-black/70"
                aria-label="Next image"
              >
                <FaChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Thumbnails */}
          <div className="grid grid-cols-5 gap-3">
            {thumbBase.map((src, i) => {
              const isActive = i === idx;
              return (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`relative rounded-lg overflow-hidden border ${
                    isActive ? "border-sky-600 ring-2 ring-sky-300" : "border-neutral-200"
                  }`}
                  aria-label={`Show image ${i + 1}`}
                >
                  <SafeImg
                    src={src}
                    alt={`${project.title} thumbnail ${i + 1}`}
                    className="w-full h-16 sm:h-20 object-cover"
                    fallback={THUMB_FALLBACK}
                  />
                </button>
              );
            })}

            {hasOverflow && (
              <button
                onClick={() => setIdx(4)}
                className={`relative rounded-lg overflow-hidden border ${
                  idx >= 4 ? "border-sky-600 ring-2 ring-sky-300" : "border-neutral-200"
                }`}
                aria-label={`Show more images`}
              >
                <SafeImg
                  src={images[4]}
                  alt={`${project.title} thumbnail 5`}
                  className="w-full h-16 sm:h-20 object-cover brightness-75"
                  fallback={THUMB_FALLBACK}
                />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="px-2 py-1 text-sm font-semibold bg-black/60 text-white rounded-md">
                    +{images.length - 4}
                  </span>
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Pagination */
function Pagination({ page, pageCount, onChange }) {
  if (pageCount <= 1) return null;
  const nums = [...Array(pageCount)].map((_, i) => i + 1);
  return (
    <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className={`px-3 py-2 rounded border text-sm font-medium ${
          page === 1
            ? "text-gray-400 border-gray-200 cursor-not-allowed"
            : "text-neutral-800 border-neutral-300 hover:bg-neutral-100"
        }`}
      >
        Prev
      </button>
      {nums.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`w-9 h-9 rounded border text-sm font-semibold ${
            n === page
              ? "bg-sky-600 border-sky-600 text-white"
              : "border-neutral-300 text-neutral-800 hover:bg-neutral-100"
          }`}
          aria-current={n === page ? "page" : undefined}
        >
          {n}
        </button>
      ))}
      <button
        type="button"
        onClick={() => onChange(Math.min(pageCount, page + 1))}
        disabled={page === pageCount}
        className={`px-3 py-2 rounded border text-sm font-medium ${
          page === pageCount
            ? "text-gray-400 border-gray-200 cursor-not-allowed"
            : "text-neutral-800 border-neutral-300 hover:bg-neutral-100"
        }`}
      >
        Next
      </button>
    </div>
  );
}

/* Page */
export default function OurProjects() {
  const [ongoing, setOngoing] = useState([]);
  const [finished, setFinished] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const [ongoingPage, setOngoingPage] = useState(1);
  const [finishedPage, setFinishedPage] = useState(1);

  // Load once on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [og, fn] = await Promise.all([
          fetchProjects("ongoing"),
          fetchProjects("finished"),
        ]);
        if (mounted) {
          setOngoing(og);
          setFinished(fn);
        }
      } catch (e) {
        console.error("Could not load projects", e);
      } finally {
        mounted && setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [selected]);

  const PER_ONGOING = 3;
  const PER_FINISHED = 6;

  const ongoingPageCount = Math.ceil(Math.max(1, ongoing.length) / PER_ONGOING) || 1;
  const finishedPageCount = Math.ceil(Math.max(1, finished.length) / PER_FINISHED) || 1;

  const ongoingPaged = useMemo(() => {
    const start = (ongoingPage - 1) * PER_ONGOING;
    return ongoing.slice(start, start + PER_ONGOING);
  }, [ongoing, ongoingPage]);

  const finishedPaged = useMemo(() => {
    const start = (finishedPage - 1) * PER_FINISHED;
    return finished.slice(start, start + PER_FINISHED);
  }, [finished, finishedPage]);

  return (
    <main className="bg-white">
      {/* HERO */}
      <section className="relative">
        <div
          className="h-[360px] bg-cover bg-center"
          style={{ backgroundImage: "url('/images/projects.jpg')" }}
        />
        <div className="absolute inset-0 bg-neutral-900/70" aria-hidden="true" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
            <div className="max-w-3xl text-white text-left">
              <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">Our Projects</h1>
              <p className="mt-3 text-xl text-white/85">
                Explore our ongoing and completed works planned with precision and delivered to exacting standards.
              </p>
              <a
                href="#Finished Projects"
                className="inline-block mt-6 px-6 py-3 rounded bg-cyan-400 font-semibold text-black hover:bg-cyan-300"
              >
                VIEW OUR WORK
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-10">
        <h2 className="text-xl md:text-3xl font-extrabold text-neutral-900 text-center">On Going Projects</h2>

        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {(loading && !ongoing.length ? Array.from({ length: 3 }) : ongoingPaged).map((p, i) =>
            loading && !ongoing.length ? (
              <div key={`s-${i}`} className="border mt-3 rounded-sm bg-white shadow animate-pulse">
                <div className="h-56 w-full bg-neutral-200" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-neutral-200 rounded w-3/4" />
                  <div className="h-4 bg-neutral-200 rounded w-full" />
                  <div className="h-8 bg-neutral-200 rounded w-28 ml-auto" />
                </div>
              </div>
            ) : (
              <ProjectCard key={p.id} p={p} onOpen={setSelected} />
            )
          )}
        </div>
        <Pagination page={ongoingPage} pageCount={ongoingPageCount} onChange={setOngoingPage} />

        <h2 className="mt-12 md:mt-16 text-xl md:text-3xl font-extrabold text-neutral-900 text-center">Completed Projects</h2>

        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {(loading && !finished.length ? Array.from({ length: 6 }) : finishedPaged).map((p, i) =>
            loading && !finished.length ? (
              <div key={`sf-${i}`} className="border mt-3 rounded-sm bg-white shadow animate-pulse">
                <div className="h-56 w-full bg-neutral-200" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-neutral-200 rounded w-3/4" />
                  <div className="h-4 bg-neutral-200 rounded w-full" />
                  <div className="h-8 bg-neutral-200 rounded w-28 ml-auto" />
                </div>
              </div>
            ) : (
              <ProjectCard key={p.id} p={p} onOpen={setSelected} />
            )
          )}
        </div>
        <Pagination page={finishedPage} pageCount={finishedPageCount} onChange={setFinishedPage} />
      </section>

      <ProjectModal project={selected} onClose={() => setSelected(null)} />
    </main>
  );
}
